/**
 * Pure date + derivation helpers for the Creche & Hotel schedule.
 * Kept free of React/Supabase so the grid maths can be unit tested.
 */

import type { StayPack } from "@/hooks/useStayPacks";

export type StayShowFilter = "all" | "daycare" | "hotel";

/** Mirrors stay_reservations.module_type. */
export type StayNature = "daycare" | "hotel" | "both";

/** Mirrors stay_reservations.status — the reservation's own lifecycle, not any single day's. */
export type StayStatus = "reserved" | "checked_in" | "checked_out" | "cancelled" | "no_show";

export type StayPaymentState = "paid_pack" | "paid_single" | "plan" | "due";

export interface ScheduleReservation {
  id: string;
  petId: string;
  petName: string;
  ownerId: string | null;
  ownerName: string;
  nature: StayNature;
  checkInDate: string;
  checkOutDate: string | null;
  checkInTime: string | null;
  /** `morning` | `evening` quando a clínica usa dois turnos. */
  arrivalShift?: "morning" | "evening" | null;
  status: StayStatus;
  isOvernight: boolean;
  bedLabel: string | null;
  /** `null` = sem plano ativo. `paid` reflete a venda da charge do ciclo atual. */
  stayPlan: { covered: boolean; paid: boolean } | null;
  /** `pets.birth_date`, `null` quando não cadastrado. */
  birthDate: string | null;
  /**
   * Status por dia, vindo de `stay_reservation_days` ("scheduled" já mapeado
   * para "reserved"). Uma reserva de creche recorrente tem um status por dia
   * dentro do intervalo — `status` sozinho não basta para isso.
   */
  dayStatusByDate?: Record<string, StayStatus>;
  /** `stay_reservation_days.id` por data — o que check-in/check-out grava agora. */
  dayIdByDate?: Record<string, string>;
}

/** O status efetivo de uma reserva num dia específico, para exibição na lista do dia. */
export function resolveDisplayStatus(reservation: ScheduleReservation, isoDate: string): StayStatus {
  return reservation.dayStatusByDate?.[isoDate] ?? reservation.status;
}

export interface ScheduleDayCounts {
  daycare: number;
  hotel: number;
}

/** Local calendar day as `YYYY-MM-DD` — never UTC, so "today" matches the clinic's clock. */
export const toIsoDate = (date: Date) => date.toLocaleDateString("en-CA");

/** Parses `YYYY-MM-DD` at midday so DST shifts can never move the day. */
export const parseIsoDate = (value: string) => new Date(`${value}T12:00:00`);

export function isStayPackActiveOnDate(pack: StayPack, date: string) {
  const recurrence = pack.recurrence;
  if (!pack.isActive || !recurrence || !date || (recurrence.endsOn && date > recurrence.endsOn)) return false;

  const selectedDate = parseIsoDate(date);
  if (Number.isNaN(selectedDate.getTime())) return false;

  if (recurrence.frequency === "daily") return true;
  if (recurrence.frequency === "weekly") return recurrence.daysOfWeek.includes(selectedDate.getDay());
  if (recurrence.frequency === "monthly") return recurrence.daysOfMonth.includes(selectedDate.getDate());

  // A pack set to every N days has no anchor date in its current data model,
  // so it cannot be safely presented as active for an arbitrary reservation date.
  return false;
}

export const addDays = (date: Date, amount: number) => {
  const next = new Date(date);
  next.setDate(next.getDate() + amount);
  return next;
};

export const addMonths = (date: Date, amount: number) =>
  new Date(date.getFullYear(), date.getMonth() + amount, 1);

/** Sunday-first, matching the Dom…Sáb header row. */
export const startOfWeek = (date: Date) => addDays(date, -date.getDay());

export const startOfMonth = (date: Date) => new Date(date.getFullYear(), date.getMonth(), 1);

export const daysInMonth = (date: Date) =>
  new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();

/**
 * Month cells for the 7-column grid: `null` entries are the leading blanks that
 * push day 1 under its weekday column.
 */
export function monthGridCells(anchor: Date): Array<string | null> {
  const first = startOfMonth(anchor);
  const cells: Array<string | null> = Array.from({ length: first.getDay() }, () => null);
  for (let day = 1; day <= daysInMonth(anchor); day += 1) {
    cells.push(toIsoDate(new Date(anchor.getFullYear(), anchor.getMonth(), day)));
  }
  return cells;
}

/** The seven Sunday–Saturday days of the week containing `anchor`. */
export function weekCells(anchor: Date): string[] {
  const sunday = startOfWeek(anchor);
  return Array.from({ length: 7 }, (_, index) => toIsoDate(addDays(sunday, index)));
}

/**
 * A stay occupies every day from check-in through check-out inclusive; an open
 * stay (no check-out yet) occupies only its check-in day. A recurring stay
 * instead has a materialized day for every occurrence, including ones on
 * dates other than the original check-in — those must count as covered too,
 * or the calendar only ever shows the day the reservation was created on.
 */
export function coversDate(reservation: ScheduleReservation, isoDate: string) {
  if (reservation.dayStatusByDate && isoDate in reservation.dayStatusByDate) return true;
  const end = reservation.checkOutDate ?? reservation.checkInDate;
  return reservation.checkInDate <= isoDate && isoDate <= end;
}

/** True when at least one materialized stay day belongs to the visible range. */
export function hasStayDayInRange(reservation: ScheduleReservation, from: string, to: string) {
  return Object.keys(reservation.dayStatusByDate ?? {}).some((stayDate) => stayDate >= from && stayDate <= to);
}

export function matchesShowFilter(nature: StayNature, show: StayShowFilter) {
  if (show === "all") return true;
  if (show === "daycare") return nature === "daycare" || nature === "both";
  return nature === "hotel" || nature === "both";
}

export function reservationsOnDate(
  reservations: ScheduleReservation[],
  isoDate: string,
  show: StayShowFilter,
) {
  return reservations.filter(
    (reservation) => coversDate(reservation, isoDate) && matchesShowFilter(reservation.nature, show),
  );
}

/** Month/day equality on `YYYY-MM-DD` strings — birthdays repeat every year. */
export function isBirthdayOnDate(birthDate: string | null, isoDate: string): boolean {
  if (!birthDate) return false;
  return birthDate.slice(5, 10) === isoDate.slice(5, 10);
}

export function birthdayReservationsOnDate(
  reservations: ScheduleReservation[],
  isoDate: string,
  show: StayShowFilter,
): ScheduleReservation[] {
  return reservationsOnDate(reservations, isoDate, show).filter((reservation) =>
    isBirthdayOnDate(reservation.birthDate, isoDate),
  );
}

/** A pet with an active daycare/hotel plan contract, independent of any reservation on a given day. */
export interface PlanBirthdayPet {
  petId: string;
  petName: string;
  ownerName: string;
  birthDate: string | null;
  module: "daycare" | "hotel";
}

export interface BirthdayPet {
  petId: string;
  petName: string;
  ownerName: string;
}

/**
 * Union of birthdays from reservations covering the day and from pets with an
 * active plan (regardless of whether they have a reservation that day),
 * deduplicated by pet.
 */
export function birthdayPetsOnDate(
  reservations: ScheduleReservation[],
  planPets: PlanBirthdayPet[],
  isoDate: string,
  show: StayShowFilter,
): BirthdayPet[] {
  const fromReservations = birthdayReservationsOnDate(reservations, isoDate, show).map((reservation) => ({
    petId: reservation.petId,
    petName: reservation.petName,
    ownerName: reservation.ownerName,
  }));

  const fromPlans = planPets
    .filter((pet) => isBirthdayOnDate(pet.birthDate, isoDate) && matchesShowFilter(pet.module, show))
    .map((pet) => ({ petId: pet.petId, petName: pet.petName, ownerName: pet.ownerName }));

  const byPetId = new Map<string, BirthdayPet>();
  for (const pet of [...fromReservations, ...fromPlans]) byPetId.set(pet.petId, pet);
  return [...byPetId.values()];
}

export function countsOnDate(
  reservations: ScheduleReservation[],
  isoDate: string,
  show: StayShowFilter,
): ScheduleDayCounts {
  const onDate = reservationsOnDate(reservations, isoDate, show);
  return {
    daycare: onDate.filter((reservation) => reservation.nature !== "hotel").length,
    hotel: onDate.filter((reservation) => reservation.nature !== "daycare").length,
  };
}

/**
 * Payment read-out for the day list. Coverage by a stay plan is what
 * separates a settled stay from one the front desk still has to charge —
 * this applies before check-in too, since a future reservation without a
 * plan is still money the clinic will need to collect.
 */
export function paymentState(reservation: ScheduleReservation): StayPaymentState {
  if (!reservation.stayPlan?.covered) return "due";
  return reservation.stayPlan.paid ? "paid_pack" : "plan";
}

/** `17→21/06` for a multi-day stay, `—` for a single daycare day. */
export function formatStayRange(reservation: ScheduleReservation) {
  if (!reservation.checkOutDate || reservation.checkOutDate === reservation.checkInDate) return null;
  const start = parseIsoDate(reservation.checkInDate);
  const end = parseIsoDate(reservation.checkOutDate);
  const pad = (value: number) => String(value).padStart(2, "0");
  return `${pad(start.getDate())}→${pad(end.getDate())}/${pad(end.getMonth() + 1)}`;
}

/** `07h45` — the arrival read-out; `null` when the stay has no scheduled arrival. */
export function formatArrival(checkInTime: string | null) {
  if (!checkInTime) return null;
  const [hours, minutes] = checkInTime.split(":");
  if (!hours || !minutes) return null;
  return `${hours}h${minutes}`;
}

export function initialsOf(name: string) {
  const trimmed = name.trim();
  if (!trimmed) return "??";
  return trimmed.slice(0, 2).toUpperCase();
}
